/* Cairn Press: "Eleven slices" ridgeline story.
   The map is a CSS-projected <img>; slices, cuts and type are drawn on one canvas with the
   same projection, so the end frame is the print footer exactly (same rows, spacing, colours). */
(() => {
  const root = document.querySelector('[data-ridge]');
  if (!root) return;
  const stage = root.querySelector('.ridge-stage'), scene = root.querySelector('.ridge-scene');
  const canvas = scene.querySelector('.ridge-canvas'), ctx = canvas.getContext('2d');
  const paper = scene.querySelector('.ridge-paper'), shade = scene.querySelector('.ridge-shade'), bar = root.querySelector('.ridge-progress');
  const steps = [...root.querySelectorAll('.ridge-steps li')];
  const maps = { ivory: scene.querySelector('.ridge-map--ivory'), midnight: scene.querySelector('.ridge-map--midnight') };
  const data = JSON.parse(root.querySelector('.ridge-data').textContent);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  const ED = {
    ivory: { bg: [245, 242, 234], ink: [31, 42, 51], det: .7, dim: .42 },
    midnight: { bg: [23, 34, 44], ink: [237, 230, 214], det: .85, dim: .3 }
  };
  const STAGE = [20, 41, 42], CUT = [219, 196, 120];
  // Print geometry in sheet units (2000 x 2828), as make_v2.py footer_ridgelines().
  const AX = 130, AY = 360, AW = 1740, AH = 1720, BCX = AX + AW / 2, BCY = AY + AH / 2;
  const FX0 = 380, FX1 = 1620, BASE = 2340, GAP = 14, VS = 120, S3 = 360;
  const K = data.rows.length, N = data.profiles[0].length;
  const lows = data.profiles.map(p => Math.min(...p));
  const shift = BASE - Math.max(...lows.map((lo, i) => BASE - (K - 1 - i) * GAP - lo * VS));
  const baseY = lows.map((_, i) => BASE - (K - 1 - i) * GAP + shift);

  const clamp = v => v < 0 ? 0 : v > 1 ? 1 : v, ph = (p, a, b) => clamp((p - a) / (b - a));
  const eio = v => v < .5 ? 4 * v * v * v : 1 - Math.pow(-2 * v + 2, 3) / 2, ease = v => v * v * (3 - 2 * v);
  const lerp = (a, b, t) => a + (b - a) * t, rgb = (c, a = 1) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;
  const mixc = (ink, bg, t) => ink.map((v, i) => bg[i] + (v - bg[i]) * t);

  // Camera framings: sheet-space rectangles that should fill the scene, keyed by progress.
  const FR = [
    [0, 1000, 1220, 1800, 1780], [.05, 1000, 1220, 1800, 1780],
    [.3, 1000, 1150, 2150, 1600], [.55, 1000, 1150, 2150, 1600],
    [.72, 1000, 1170, 2000, 900], [.74, 1000, 1170, 2000, 900],
    [.85, 1000, 2240, 1640, 720], [.89, 1000, 2240, 1640, 720],
    [.98, 1000, 1414, 2180, 3120], [1, 1000, 1414, 2180, 3120]
  ];
  let W = 0, H = 0, dpr = 1, D = 2000, mobile = false, edition = root.dataset.edition || 'ivory';
  let fontsReady = false, lastP = -1, frame = 0, inset = 0, nameFs = 70, nameW = [], detW = [];

  function camera(p) {
    let i = 0; while (i < FR.length - 2 && p > FR[i + 1][0]) i++;
    const a = FR[i], b = FR[i + 1], t = eio(ph(p, a[0], b[0]));
    const fit = f => Math.min(W * (mobile ? .96 : .92) / f[3], H * .92 / f[4]);
    return { cx: lerp(a[1], b[1], t), cy: lerp(a[2], b[2], t), k: Math.exp(lerp(Math.log(fit(a)), Math.log(fit(b)), t)) };
  }

  function pose(p) {
    // tilt back, swing round to eye level (the ground side-on), then settle flat into the print
    const tilt = eio(ph(p, .05, .3)), side = eio(ph(p, .55, .73)), g = eio(ph(p, .7, .84));
    return {
      p, g, side, S: lerp(S3, 121, side),
      th: (58 * tilt + 24 * side) * Math.PI / 180, fi: -11 * eio(ph(p, .08, .34)) * (1 - side) * Math.PI / 180,
      dim: ph(p, .2, .34) * (1 - ph(p, .55, .64)), mapA: p < .75 ? 1 - ph(p, .55, .66) : ease(ph(p, .92, .98)), cutA: 1 - ph(p, .55, .63), cam: camera(p)
    };
  }

  // board-local (bx, by, bz) -> camera space (X, Y, Z) in px around the scene centre
  function board(s, bx, by, bz) {
    const ct = Math.cos(s.th), st = Math.sin(s.th), cf = Math.cos(s.fi), sf = Math.sin(s.fi);
    const x = AX + bx - BCX, y = AY + by - BCY;
    const x1 = x * cf - y * sf, y1 = x * sf + y * cf, z1 = bz;          // rotateZ
    const y2 = y1 * ct - z1 * st, z2 = y1 * st + z1 * ct;                // rotateX
    const c = s.cam;
    return [(x1 + BCX - c.cx) * c.k, (y2 + BCY - c.cy) * c.k, z2 * c.k];
  }
  const flat = (s, x, y) => [(x - s.cam.cx) * s.cam.k, (y - s.cam.cy) * s.cam.k, 0];
  const proj = q => { const f = D / (D - q[2]); return [W / 2 + q[0] * f, H / 2 + q[1] * f]; };
  const mix3 = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

  function mapMatrix(s, img) {
    // img px -> board units -> rotate -> camera -> perspective -> scene px (column-major for matrix3d)
    const c = s.cam, k = c.k, ct = Math.cos(s.th), st = Math.sin(s.th), cf = Math.cos(s.fi), sf = Math.sin(s.fi);
    const ax = AW / img.width, ay = AH / img.height, ox = AX - BCX, oy = AY - BCY;
    // linear part acting on (ix, iy): rotation R = Rx*Rz applied to (ax*ix + ox, ay*iy + oy, 0)
    const R = [[cf, -sf, 0], [ct * sf, ct * cf, -st], [st * sf, st * cf, ct]];
    const col = (j, sc) => [R[0][j] * sc, R[1][j] * sc, R[2][j] * sc];
    const ex = col(0, ax), ey = col(1, ay), o = [R[0][0] * ox + R[0][1] * oy, R[1][0] * ox + R[1][1] * oy, R[2][0] * ox + R[2][1] * oy];
    // camera: X = k*(v.x + BCX - cx), Y = k*(v.y + BCY - cy), Z = k*v.z
    const cam = v => [k * v[0], k * v[1], k * v[2]], tx = k * (o[0] + BCX - c.cx), ty = k * (o[1] + BCY - c.cy), tz = k * o[2];
    const X = cam(ex), Y = cam(ey);
    // homogeneous: w = 1 - Z/D; screen = (X + W/2*w, Y + H/2*w)
    const m = (vx, vy, vz, t) => { const w = (t ? 1 : 0) - vz / D; return [vx + W / 2 * w, vy + H / 2 * w, vz, w]; };
    const c0 = m(X[0], X[1], X[2], 0), c1 = m(Y[0], Y[1], Y[2], 0), c3 = m(tx, ty, tz, 1);
    return `matrix3d(${c0.join(',')},${c1.join(',')},0,0,1,0,${c3.join(',')})`;
  }

  function measureText() {
    if (!fontsReady) return;
    ctx.save(); ctx.font = `500 100px "Cairn Jost", Jost, sans-serif`;
    const n = data.name, raw = [...n].reduce((a, ch) => a + ctx.measureText(ch).width, 0) * .7 + 28 * (n.length - 1);
    nameFs = 70 * Math.min(1, 1640 / raw);
    ctx.font = `500 ${nameFs}px "Cairn Jost", Jost, sans-serif`; nameW = [...n].map(ch => ctx.measureText(ch).width);
    ctx.font = `300 26px "Cairn Jost", Jost, sans-serif`; detW = [...data.details].map(ch => ctx.measureText(ch).width);
    ctx.restore();
  }

  function spaced(s, str, widths, fs, ls, y, weight, color, reveal) {
    const total = widths.reduce((a, b) => a + b, 0) + ls * (widths.length - 1);
    let x = 1000 - total / 2;
    ctx.font = `${weight} ${fs * s.cam.k}px "Cairn Jost", Jost, sans-serif`;
    [...str].forEach((ch, j) => {
      const a = reveal(j, widths.length);
      if (a > 0.003 && ch !== ' ') {
        const q = proj(flat(s, x, y + (1 - a) * 18));
        ctx.fillStyle = rgb(color, a); ctx.fillText(ch, q[0], q[1]);
      }
      x += widths[j] + ls;
    });
  }

  function render(p) {
    if (!W) return;
    const s = pose(p), e = ED[edition], img = maps[edition] || maps.ivory;
    if (img) { img.style.transform = mapMatrix(p > .75 ? { ...s, th: 0, fi: 0 } : s, { width: 1300, height: 1285 }); img.style.opacity = s.mapA; }
    // the sheet grows out from the ridgeline block to the full print
    const card = ease(ph(p, .815, .85)), open = eio(ph(p, .89, .97)), sheetM = `matrix(${s.cam.k},0,0,${s.cam.k},${W / 2 - s.cam.cx * s.cam.k},${H / 2 - s.cam.cy * s.cam.k})`;
    paper.style.opacity = card;
    paper.style.background = rgb(e.bg);
    paper.style.clipPath = `inset(${lerp(2060, 0, open)}px ${lerp(300, 0, open)}px ${lerp(250, 0, open)}px ${lerp(300, 0, open)}px)`;
    paper.style.transform = sheetM;
    if (shade) { shade.style.opacity = ease(ph(p, .92, 1)); shade.style.transform = sheetM; }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';

    // dim the ground so the standing slices read
    if (s.dim > 0) {
      const c = [[0, 0], [AW, 0], [AW, AH], [0, AH]].map(([x, y]) => proj(board(s, x, y, 0)));
      ctx.beginPath(); c.forEach((q, i) => i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1])); ctx.closePath();
      ctx.fillStyle = rgb(STAGE, e.dim * s.dim); ctx.fill();
    }

    // cut lines, swept west to east
    if (s.cutA > 0) for (let i = 0; i < K; i++) {
      const sw = eio(ph(p, .1 + i * .014, .24 + i * .014)); if (sw <= 0) continue;
      const by = data.rows[i] * AH, a = proj(board(s, 0, by, 0)), b = proj(board(s, sw * AW, by, 0));
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]);
      ctx.strokeStyle = rgb(CUT, .9 * s.cutA); ctx.lineWidth = 1.25; ctx.stroke();
      if (sw < 1) {
        ctx.beginPath(); ctx.arc(b[0], b[1], 3.2, 0, 7); ctx.fillStyle = rgb(CUT, s.cutA);
        ctx.shadowColor = rgb(CUT, .9); ctx.shadowBlur = 14; ctx.fill(); ctx.shadowBlur = 0;
      }
    }

    // the eleven slices, far (north) to near
    const lwEnd = Math.max(.75, 2 * s.cam.k);
    for (let i = 0; i < K; i++) {
      const rise = eio(ph(p, .25 + i * .017, .36 + i * .017)), prof = data.profiles[i];
      if (rise <= 0 && s.g <= 0) continue;
      const g = s.g, by = data.rows[i] * AH;
      const pts = new Array(N);
      for (let j = 0; j < N; j++) {
        const u = j / (N - 1), v = prof[j];
        const a = g < 1 ? board(s, u * AW, by, v * s.S * rise) : null;
        const b = g > 0 ? flat(s, FX0 + u * (FX1 - FX0), baseY[i] - v * VS) : null;
        pts[j] = proj(!b ? a : !a ? b : mix3(a, b, g));
      }
      const foot = u => { const a = g < 1 ? board(s, u * AW, by, 0) : null, b = g > 0 ? flat(s, FX0 + u * (FX1 - FX0), baseY[i] + GAP) : null; return proj(!b ? a : !a ? b : mix3(a, b, g)); };
      const f1 = foot(1), f0 = foot(0);
      const t = lerp(.96, .35 + .55 * i / (K - 1), g), col = mixc(e.ink, e.bg, t);
      // wall
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let j = 1; j < N; j++) ctx.lineTo(pts[j][0], pts[j][1]);
      ctx.lineTo(f1[0], f1[1]); ctx.lineTo(f0[0], f0[1]); ctx.closePath();
      ctx.fillStyle = rgb(e.bg, lerp(.8, 1, g) * Math.min(1, rise * 3 + g)); ctx.fill();
      // ridge line, faded at both ends as on the print
      const gr = ctx.createLinearGradient(pts[0][0], pts[0][1], pts[N - 1][0], pts[N - 1][1]);
      const la = Math.min(1, rise * 2 + g);
      gr.addColorStop(0, rgb(col, 0)); gr.addColorStop(.1, rgb(col, la)); gr.addColorStop(.9, rgb(col, la)); gr.addColorStop(1, rgb(col, 0));
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let j = 1; j < N; j++) ctx.lineTo(pts[j][0], pts[j][1]);
      ctx.strokeStyle = gr; ctx.lineWidth = lerp(1.7, lwEnd, g); ctx.stroke();
    }

    // name and details, as set under every print
    if (fontsReady && p > .8) {
      ctx.textBaseline = 'alphabetic';
      spaced(s, data.name, nameW, nameFs, 28, 2470, 500, e.ink, (j, n) => eio(ph(p, .84 + j * .005, .875 + j * .005)));
      spaced(s, data.details, detW, 26, 10, 2530, 300, mixc(e.ink, e.bg, e.det), () => eio(ph(p, .875, .91)));
    }
  }

  function updateCopy(p) {
    const at = p < .25 ? 0 : p < .55 ? 1 : 2, spans = [[.04, .25], [.25, .55], [.55, .92]];
    steps.forEach((li, i) => {
      li.classList.toggle('is-active', i === at);
      li.style.setProperty('--fill', reduced.matches ? 1 : ph(p, spans[i][0], spans[i][1]).toFixed(3));
    });
    if (bar) bar.style.transform = `scaleX(${p})`;
  }

  function size() {
    const r = scene.getBoundingClientRect();
    mobile = innerWidth <= 700;
    W = Math.round(r.width); H = Math.round(r.height);
    dpr = Math.min(2, devicePixelRatio || 1);
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    D = Math.max(W, H) * 2.4;
    inset = parseFloat(getComputedStyle(stage).top) || 0;
    lastP = -1;
  }

  function progress() {
    if (reduced.matches) return 1;
    const r = root.getBoundingClientRect(), span = r.height - stage.offsetHeight;
    return clamp((inset - r.top) / Math.max(1, span));
  }

  function draw() {
    frame = 0;
    const r = root.getBoundingClientRect();
    if (r.bottom < -50 || r.top > innerHeight + 50) return;
    const p = progress();
    if (p === lastP) return;
    lastP = p; render(p); updateCopy(p);
  }
  const queue = () => { if (!frame) frame = requestAnimationFrame(draw); };
  const invalidate = () => { size(); queue(); };

  root.querySelectorAll('[data-ridge-edition]').forEach(btn => btn.addEventListener('click', () => {
    edition = btn.dataset.ridgeEdition;
    root.dataset.edition = edition;
    root.querySelectorAll('[data-ridge-edition]').forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
    Object.entries(maps).forEach(([k, m]) => m && m.classList.toggle('is-current', k === edition));
    lastP = -1; queue();
  }));

  root.classList.add('is-live');
  size();
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', invalidate, { passive: true });
  reduced.addEventListener('change', invalidate);
  new ResizeObserver(invalidate).observe(scene);
  Object.values(maps).forEach(m => m && !m.complete && m.addEventListener('load', () => { lastP = -1; queue(); }, { once: true }));
  Promise.all([document.fonts.load('500 40px "Cairn Jost"'), document.fonts.load('300 40px "Cairn Jost"')])
    .catch(() => {}).then(() => { fontsReady = true; measureText(); lastP = -1; queue(); });
  queue();
})();
